import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, X } from "lucide-react";
import { runJob } from "@/lib/pollJob";

/**
 * Modal that fetches the rendered email HTML from a preview endpoint and
 * displays it inside a sandboxed iframe so users can see exactly what will
 * hit their inbox. With `poll`, previewUrl is a job-start endpoint and the
 * result is polled (long builds would otherwise hit the prod edge 30s cap).
 */
export default function EmailPreviewModal({ open, onClose, previewUrl, title, poll = false }) {
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !previewUrl) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    setHtml("");
    const fetcher = poll
      ? runJob(previewUrl, undefined, { timeoutMs: 180000 })
      : axios.post(previewUrl).then((r) => r.data);
    fetcher
      .then((data) => {
        if (cancelled) return;
        // Inject `<base target="_top">` so anchor clicks open in a new top
        // window instead of trying to load inside the sandboxed iframe (which
        // blocks scripts and shows a "JS required" error for our React routes).
        const raw = data?.html || "";
        setHtml(raw.replace(/<head([^>]*)>/i, '<head$1><base target="_top">'));
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "Couldn't build a preview right now.");
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, previewUrl, poll]);

  // Close on escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid="email-preview-modal"
    >
      <div
        className="w-full max-w-3xl max-h-[90vh] flex flex-col bg-white border-2 border-black shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b-2 border-black bg-[#0A0A0A] text-white">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#FFD600] font-semibold">Email preview</div>
            <div className="text-sm font-semibold truncate" title={title}>{title || "Your digest"}</div>
          </div>
          <button
            onClick={onClose}
            data-testid="email-preview-close"
            aria-label="Close preview"
            className="inline-flex items-center justify-center w-9 h-9 border border-white/30 hover:bg-white hover:text-black transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-hidden bg-[#F9F9F9]">
          {loading && (
            <div className="h-full min-h-[360px] flex flex-col items-center justify-center text-center px-6" data-testid="email-preview-loading">
              <Loader2 className="w-8 h-8 animate-spin mb-4" />
              <div className="ticker-label !text-[10px] mb-1">Building your preview</div>
              <p className="text-sm text-neutral-700 max-w-sm">
                Pulling live data and rendering the email exactly as it would arrive in your inbox. Takes a few seconds.
              </p>
            </div>
          )}
          {!loading && error && (
            <div className="h-full min-h-[360px] flex flex-col items-center justify-center text-center px-6" data-testid="email-preview-error">
              <div className="ticker-label !text-[10px] mb-2">Hmm</div>
              <h2 className="font-display text-2xl font-black tracking-tighter mb-2">We couldn&rsquo;t build that preview.</h2>
              <p className="text-sm text-neutral-700 max-w-sm">{error}</p>
            </div>
          )}
          {!loading && !error && html && (
            <iframe
              title="Email preview"
              data-testid="email-preview-iframe"
              srcDoc={html}
              sandbox=""
              className="w-full h-[72vh] bg-white border-0"
            />
          )}
        </div>
      </div>
    </div>
  );
}
